import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { FB_ID_LOCAL_STORAGE } from 'constants/api';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { logError } from 'utils/logError';
import { GET_USER } from '../sessionProvider.api';
import { SESSION_STATE } from '../sessionProvider.constants';
import {
  IUserQuery,
  SessionUser,
  TSessionState,
} from '../sessionProvider.types';

export const useSessionUser = () => {
  // DB user
  const [user, setUser] = useState<SessionUser>();
  const [sessionState, setSessionState] = useState<TSessionState>(
    SESSION_STATE.UNAUTHENTICATED,
  );
  const [isUploading, setIsUploading] = useState(false);

  const hasInstitutionAccessScope = Boolean(
    user?.institution &&
      'dataAccessScope' in user.institution &&
      user.institution.dataAccessScope === 'institution',
  );

  const storedFirebaseId = sessionStorage.getItem(FB_ID_LOCAL_STORAGE);

  const [firebaseId, setFirebaseId] = useState<string | null>(storedFirebaseId);

  const { refetch, loading } = useQuery<IUserQuery>(GET_USER, {
    variables: {
      filter: { firebaseId },
    },
    skip:
      sessionState === SESSION_STATE.LOGIN_PENDING_ACTION ||
      !firebaseId ||
      !!user,
    onCompleted: ({ user: responseUser }) => {
      setUser(responseUser);
      setSessionState(SESSION_STATE.AUTHENTICATED);
      if (responseUser.timezone) {
        dayjs.tz.setDefault(responseUser.timezone);
      }
    },
    onError: (error) => {
      logError({ error });
    },
  });

  useEffect(() => {
    if (sessionState === SESSION_STATE.AUTHENTICATED) {
      void refetch();
    }
  }, [sessionState, refetch]);

  useEffect(() => {
    Sentry.setUser({
      userId: user?._id.toString(),
      institutionId: user?.institution._id.toString(),
      sessionState,
    });
  }, [user, sessionState]);

  return {
    firebaseId,
    setFirebaseId,
    user,
    setUser,
    loading,
    hasInstitutionAccessScope,
    sessionState,
    setSessionState,
    isUploading,
    setIsUploading,
  };
};
